import { useTranslation } from 'react-i18next';

import { MultiSelect } from '../../../../../../shared/components/UI/MultiSelect/MultiSelect';

import type { BidResponse } from '../../../../../../shared/entities/BidResponse';
import type { VaultProperty } from '../../../../../../shared/entities/VaultProperty';

export type SelectPropertyProps = {
  bidResponse: BidResponse;
  vaultProperty: VaultProperty;
  deleteBidResponseTag: (props: {
    bidResponseId: number;
    propertyId: number;
    tagId: number;
  }) => void;
  createBidResponseTag: (props: BidResponseTagHandlerProps) => void;
};

export type BidResponseTagHandlerProps = {
  bidResponseId: number;
  propertyId: number;
  optionId: number;
};

export function SingleSelectProperty({
  bidResponse,
  vaultProperty,
  deleteBidResponseTag,
  createBidResponseTag,
}: SelectPropertyProps) {
  const { t } = useTranslation('bidResponse');

  const selectedTag = bidResponse.properties.find(
    p => p.propertyId === vaultProperty.id,
  )?.values[0];

  const selectedOption = vaultProperty.options.find(
    option => option.name === selectedTag?.name,
  );

  const handleChange = (valueId: string[]) => {
    if (valueId.length === 0 && selectedTag) {
      handleDelete(selectedTag.tagId);
      return;
    }

    const newValue = Number(valueId[valueId.length - 1]);

    if (newValue === selectedOption?.id) return;

    if (selectedTag) {
      handleDelete(selectedTag.tagId);
    }

    handleCreate(newValue);
  };

  const handleDelete = (tagId: number) => {
    deleteBidResponseTag({
      bidResponseId: bidResponse.id,
      propertyId: vaultProperty.id,
      tagId,
    });
  };

  const handleCreate = (valueId: number) => {
    const optionId = vaultProperty.options.find(
      option => option.id === Number(valueId),
    )?.id;

    if (optionId !== undefined) {
      createBidResponseTag({
        bidResponseId: bidResponse.id,
        propertyId: vaultProperty.id,
        optionId,
      });
    }
  };

  const data = vaultProperty.options.map(option => ({
    value: String(option.id),
    label: option.name,
  }));

  return (
    <MultiSelect
      key={vaultProperty.id}
      variant="filled"
      tagColor={vaultProperty.color}
      label={vaultProperty.name}
      placeholder={t(
        'vault.sidepanels.bidResponse.input.property.singleSelect.placeholder',
      )}
      data={data}
      value={selectedOption ? [String(selectedOption.id)] : []}
      onChange={handleChange}
      searchable
      nothingFound={t(
        'vault.sidepanels.bidResponse.input.property.multiSelect.nothingFound',
      )}
    />
  );
}
